document.addEventListener("DOMContentLoaded", function () {
    const root = document.getElementById("consultation-app");
    if (!root || !window.Vue) return;

    Vue.createApp({
        // Markup lives in a <script type="text/x-template"> so crawlers only see the server-rendered placeholder
        template: root.dataset.template,
        delimiters: ["[[", "]]"], // Using custom delimiters if needed
        data() {
            return {
                consultationType: consultationType, // Passed from the server
                consultationGroups: [], // Initialize as an empty array
                productData: [], // Product data that comes from teh API
                flatQuestions: [],
                previousFormData: {},
                formData: {},
                formAdvisory: {}, // Stores advisory messages for selected options
                formConfirmation: {}, // Tracks if advisory confirmation is checked
                formAdditionalDetails: {}, // Keeps track of additional details requirements
                formWarning: {}, // Stores warning messages for selected options
                additionalDetails: {}, // Stores the data entered in additional details fields
                weightUnit: "kg", // Weight unit ("kg" or "st & lbs")
                heightUnit: 'cm', // Height unit (cm or ft & in)
                products: [],
                productIds: [], // Array of product IDs
                previewImages: {}, // Preview Images
                productsToExcludeMap: {}, // key: `${questionId}_${option.id}` → array of excluded product_ids
                gpSearchPostcode: "",
                gpResults: [],
                manualGPEntry: false,
                manualGP: {
                  name: "",
                  address: "",
                  phone: "",
                  email: ""
                },
                invalidQuestionId: null,
                isFormSubmitted: false, // Determines if form is submitted
                dateSubmitted: null, // Sets the date and time of submission
                isLoading: true, // Questions are being fetched from the API
                loadError: false, // Questions failed to load; shows the retry state
            };
        },
        computed: {
            calculateBMI() {
                // Find weight and height questions in flatQuestions
                const weightQuestion = this.flatQuestions.find(q => q.question_type === 'weight');
                const heightQuestion = this.flatQuestions.find(q => q.question_type === 'height');

                if (!weightQuestion || !heightQuestion) return null;

                const weight = this.formData[weightQuestion.id];
                const height = this.formData[heightQuestion.id];

                // Check if we have both weight and height values
                if (!weight || !height) return null;

                // Get weight in kg
                let weightInKg;
                if (weight.kg) {
                    weightInKg = parseFloat(weight.kg);
                } else if (weight.st && weight.lbs) {
                    // Convert stone and pounds to kg
                    const totalLbs = (parseFloat(weight.st) * 14) + parseFloat(weight.lbs);
                    weightInKg = totalLbs / 2.20462;
                } else {
                    return null;
                }

                // Get height in meters
                let heightInM;
                if (height.cm) {
                    heightInM = parseFloat(height.cm) / 100;
                } else if (height.ft && height.in) {
                    // Convert feet and inches to meters
                    const totalInches = (parseFloat(height.ft) * 12) + parseFloat(height.in);
                    heightInM = totalInches * 0.0254;
                } else {
                    return null;
                }

                // Calculate BMI
                return weightInKg / (heightInM * heightInM);
            },

            isBMITooLow() {
                const bmi = this.calculateBMI;
                return bmi !== null && bmi < 21.5;
            },

            calculateAge(){
                const dobQuestion = this.flatQuestions.find(q => q.question_type === 'dob');
                if (!dobQuestion) return null;
                const dob = this.formData[dobQuestion.id];
                if (!dob) return null;

                const today = new Date();
                const birthDate = new Date(dob);
                let age = today.getFullYear() - birthDate.getFullYear();

                // Check if birthday hasn't occurred this year yet
                const monthDiff = today.getMonth() - birthDate.getMonth();
                if (monthDiff < 0 || (monthDiff === 0 && today.getDay() < birthDate.getDay())) {
                    age--;
                }

                return age;
            },

            isAgeInvalid() {
                const age = this.calculateAge;
                return age !== null && ( age < 18 || age > 85 );
            },

            excludedProductIds() {
                return Array.from(new Set(Object.values(this.productsToExcludeMap).flat()));
            },
            visibleProducts() {
                return this.products.filter(
                    product => !this.excludedProductIds.includes(product.product_id)
                );
            },
            isFormValid() {
                return this.validateFormBeforeSubmit() === null;
              }
        },
        methods: {

            checkFormSubmissionAge() {
                if (!this.isFormSubmitted || !this.dateSubmitted) {
                    this.resetForm();
                    return;
                }

                const submissionDate = new Date(this.dateSubmitted);
                const currentDate = new Date();
                const daysDifference = (currentDate - submissionDate) / (1000 * 60 * 60 * 24);

                if (daysDifference >= 7) {
                    this.resetForm();
                }
            },

            /**
             * Fetch and process consultation data from the API
             */
            async fetchConsultationData() {
                this.isLoading = true;
                this.loadError = false;
                try {
                    const response = await fetch(
                        `https://admin.happypharmacy.co.uk/api/v1/consultation/${this.consultationType}`,
                        {signal: AbortSignal.timeout?.(15000)} // Show the retry state rather than hanging on a stalled connection
                    );
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    const data = await response.json();
                    this.consultationGroups = data.consultation.question_groups || [];
                    this.productData = data.consultation.products || [];
                    // Flatten all questions into a single array
                    this.flatQuestions = this.consultationGroups.flatMap(group => group.questions);
                    // Initialize formData for all questions
                    this.flatQuestions.forEach((question) => {
                        if (question.question_type === 'height') {
                            this.formData[question.id] = {cm: '', ft: '', in: ''}; // Handle height specifically
                        } else if (question.question_type === "weight") {
                            this.formData[question.id] = {kg: "", st: "", lbs: ""}; // Handle weight specifically
                        } else if (question.question_type === "checkbox") {
                            this.formData[question.id] = [];
                        } else if (question.question_type === "file") {
                            this.formData[question.id] = null;
                        } else {
                            this.formData[question.id] = "";
                        }
                    });
                    console.log("Consultation data:", data);
                    if( this.previousFormData !== {} ){
                        // Merge previous form data with the new data
                        this.formData = {
                            ...this.formData,
                            ...this.previousFormData,
                        };
                    }
                } catch (error) {
                    console.error("Error fetching consultation data:", error);
                    this.loadError = true;
                } finally {
                    this.isLoading = false;
                }
                this.loadProducts();
            },
            /**
             * Handle file uploads
             */
            async handleFileUpload(event, questionId) {
                const file = event.target.files[0]; // Get the selected file

                if (!file) {
                    console.error("No file selected.");
                    return;
                }

                try {
                    // Step 1: Request a presigned URL from the backend
                    const presignedResponse = await this.getPresignedUrl(file);

                    // Step 2: Upload the file using the presigned URL
                    await this.uploadToStorage(file, presignedResponse);

                    // Step 3: Construct the uploaded file's URL and update formData
                    const fileUrl = `${presignedResponse.url}/${presignedResponse.fields.key}`;

                    // Update formData with the file's metadata
                    this.formData[questionId] = {
                        fileName: file.name,
                        fileUrl: fileUrl, // URL for the uploaded file
                    };
                    this.previewImages[questionId] = fileUrl;
                    console.log(`File uploaded successfully: ${fileUrl}`);
                } catch (error) {
                    console.error("Error uploading file:", error);
                }
            },

            async getPresignedUrl(file) {
                const response = await fetch("https://admin.happypharmacy.co.uk/shopify-proxy/presigned_url", {
                    method: "POST",
                    headers: {"Content-Type": "application/json"},
                    body: JSON.stringify({
                        filename: file.name,
                        contentType: file.type,
                    }),
                });

                if (!response.ok) {
                    throw new Error("Failed to fetch presigned URL");
                }

                // Return the presigned URL data
                return await response.json();
            },

            async uploadToStorage(file, presignedResponse) {
                // Create a FormData object for the file upload
                const formData = new FormData();

                // Add all fields required by the presigned URL
                Object.entries(presignedResponse.fields).forEach(([key, value]) => {
                    formData.append(key, value);
                });

                // Add the file itself
                formData.append("file", file);

                // Make the upload request
                const response = await fetch(presignedResponse.url, {
                    method: "POST",
                    body: formData,
                });

                if (!response.ok) {
                    throw new Error(`File upload failed with status: ${response.status}`);
                }
            },

            /**
             * Convert weight from kg to st & lbs
             */
            convertWeightToStLbs(questionId) {
                const weightKg = parseFloat(this.formData[questionId].kg || 0);
                const totalLbs = weightKg * 2.20462; // 1 kg = 2.20462 lbs
                const stone = Math.floor(totalLbs / 14); // 1 stone = 14 lbs
                const lbs = (totalLbs % 14).toFixed(1);

                // Update stone and pounds
                this.formData[questionId].st = stone;
                this.formData[questionId].lbs = lbs;
            },

            /**
             * Convert weight from st & lbs to kg
             */
            convertWeightToKg(questionId) {
                const stone = parseFloat(this.formData[questionId].st || 0);
                const lbs = parseFloat(this.formData[questionId].lbs || 0);
                const totalLbs = stone * 14 + lbs; // Convert stone to lbs and add remaining lbs
                const weightKg = (totalLbs / 2.20462).toFixed(1); // Convert lbs to kg

                // Update kg
                this.formData[questionId].kg = weightKg;
            },

            /**
             * Toggle weight unit
             */
            toggleWeightUnit() {
                this.weightUnit = this.weightUnit === "kg" ? "st & lbs" : "kg";
            },

            /**
             * Convert height from cm to ft & in
             */
            convertHeightToFtIn(questionId) {
                const heightCm = parseFloat(this.formData[questionId].cm || 0);
                const totalInches = heightCm / 2.54; // 1 cm = 2.54 inches
                const feet = Math.floor(totalInches / 12); // 1 foot = 12 inches
                const inches = (totalInches % 12).toFixed(1);

                // Update feet and inches
                this.formData[questionId].ft = feet;
                this.formData[questionId].in = inches;
            },

            /**
             * Convert height from ft & in to cm
             */
            convertHeightToCm(questionId) {
                const feet = parseFloat(this.formData[questionId].ft || 0);
                const inches = parseFloat(this.formData[questionId].in || 0);
                const totalInches = feet * 12 + inches; // Convert feet to inches and add remaining inches
                const heightCm = (totalInches * 2.54).toFixed(1); // Convert inches to cm

                // Update cm
                this.formData[questionId].cm = heightCm;
            },

            /**
             * Toggle height unit
             */
            toggleHeightUnit() {
                this.heightUnit = this.heightUnit === 'cm' ? 'ft & in' : 'cm';
            },

            isQuestionVisible(question) {
                if(question === undefined){
                    return true;
                }
                if (!question.display_options || question.display_options.length === 0) {
                    return true; // Always show the question if no display conditions exist
                }

                return question.display_options.reduce((result, condition) => {
                    const dependentAnswer = this.formData[condition.depends_on_question_id];
                    // Ensure the dependent answer exists before evaluating
                    if (dependentAnswer === undefined || dependentAnswer === null) {
                        return false; // Hide question if dependent answer is missing
                    }
                    // Check if the condition is met
                    const conditionMet = this.evaluateCondition(
                        condition.operator,
                        dependentAnswer,
                        condition.id_value,
                        condition.value
                    );
                    // Combine results based on the logical operator (AND/OR)
                    return condition.logical_operator === "AND"
                        ? result && conditionMet  // All conditions must be true
                        : result || conditionMet; // At least one condition must be true

                }, true); // Start with `true` so AND logic can fail if any condition is false

            },

            // Match the operator and verify the condition
            evaluateCondition(operator, answer, idValues, values) {
                console.log("Evaluating condition:", {operator, answer, idValues, values});
                switch (operator) {
                    case '=': // Equals condition
                        return (
                            (idValues && idValues.includes(String(answer))) || // Check id_value
                            (values && values.includes(String(answer))) // Check value
                        );
                    case '!=': // Not equals condition
                        return (
                            (idValues && !idValues.includes(String(answer))) &&
                            (values && !values.includes(String(answer)))
                        );
                    case 'exists': // Check if the answer exists
                        return Boolean(answer) === values[0]; // Value should be boolean
                    case 'includes': // For checkbox-like answers, check if `idValues` contains the answer
                        return Array.isArray(answer) && answer.some((a) => idValues.includes(a));
                    case 'not_includes': // For checkbox-like answers, check exclusion
                        return Array.isArray(answer) && !answer.some((a) => idValues.includes(a));
                    default:
                        console.warn(`Unsupported operator: ${operator}`);
                        return false;
                }
            },

            /**
             * Handle option change for radio or checkbox questions.
             * @param {string} questionId - The ID of the question being handled.
             * @param {Object} option - The option that was selected or toggled.
             * @param {string} questionType - The type of the question (e.g., 'radio' or 'checkbox').
             */
            handleOptionChange(questionId, option, questionType) {

                const exclusionKey = `${questionId}_${option.id}`;
                // Handle radio buttons
                if (questionType === "radio") {
                    // Reset all previous advisories, confirmations, and additional details for the question
                    this.formAdvisory[questionId] = {};
                    this.formWarning[questionId] = {};
                    this.formAdditionalDetails[questionId] = {};
                    this.formConfirmation[questionId] = false;

                    // Set data for the newly selected option
                    if (option.advisory_message) {
                        this.formAdvisory[questionId] = {
                            [option.id]: {
                                message: option.advisory_message,
                                requiresConfirmation: option.requires_advisory_confirmation,
                            },
                        };
                    }
                    console.log(option.risk_level);
                    if(option.risk_level === 100) {
                        if (!this.formWarning[questionId]) {
                            this.formWarning[questionId] = true;
                        }
                        this.formWarning[questionId][option.id] = true;
                    }

                    if (option.requires_additional_details) {
                        if (!this.formAdditionalDetails[questionId]) {
                            this.formAdditionalDetails[questionId] = {};
                        }
                        this.formAdditionalDetails[questionId][option.id] = true;

                        // Initialize an additional details field if not already set
                        if (!this.additionalDetails[questionId]) {
                            this.additionalDetails[questionId] = "";
                        }
                    }


                    // Clear all previous product exclusions for this question
                    Object.keys(this.productsToExcludeMap).forEach((key) => {
                        if (key.startsWith(`${questionId}_`)) {
                            delete this.productsToExcludeMap[key];
                        }
                    });

                    // Add current exclusion if any
                    if (option.exclude_products && option.exclude_products.length > 0) {
                        this.productsToExcludeMap[exclusionKey] = option.exclude_products;
                    }
                }

                // Handle checkboxes
                else if (questionType === "checkbox") {
                    const isChecked = this.formData[questionId]?.includes(option.answer_value);
                    if (isChecked) {
                        if (option.exclude_products && option.exclude_products.length > 0) {
                            this.productsToExcludeMap[exclusionKey] = option.exclude_products;
                        }
                    } else {
                        delete this.productsToExcludeMap[exclusionKey];
                    }

                    if (isChecked) {
                        // Add advisory message and additional details when the checkbox is checked
                        if (option.advisory_message) {
                            if (!this.formAdvisory[questionId]) {
                                this.formAdvisory[questionId] = {};
                            }
                            this.formAdvisory[questionId][option.id] = {
                                message: option.advisory_message,
                                requiresConfirmation: option.requires_advisory_confirmation,
                            };
                        }

                        if(option.risk_level === 100) {
                            if (!this.formWarning[questionId]) {
                                this.formWarning[questionId][option.id] = {};
                            }
                        }

                        if (option.requires_additional_details) {
                            if (!this.formAdditionalDetails[questionId]) {
                                this.formAdditionalDetails[questionId] = {};
                            }
                            this.formAdditionalDetails[questionId][option.id] = true;

                            // Initialize an additional details field
                            if (!this.additionalDetails[questionId]) {
                                this.additionalDetails[questionId] = "";
                            }
                        }
                    } else {
                        // Remove advisory message and additional details when the checkbox is unchecked
                        if (this.formAdvisory[questionId]) {
                            delete this.formAdvisory[questionId][option.id];
                        }

                        if (this.formWarning[questionId]) {
                            delete this.formWarning[questionId][option.id];
                        }

                        if (this.formAdditionalDetails[questionId]) {
                            delete this.formAdditionalDetails[questionId][option.id];
                        }

                        // Reset confirmation checkbox for this question
                        if (this.formConfirmation[questionId]) {
                            this.formConfirmation[questionId] = false;
                        }
                    }
                }
            },

            isTwoOptionRadio(question) {
                return (
                  question.question_type === 'radio' &&
                  question.options &&
                  question.options.length === 2
                );
              },

            getSelectedOptionId(question) {
              const answer = this.formData[question.id];
              const selected = question.options.find(opt => opt.answer_value === answer);
              return selected ? selected.id : null;
            },
            toggleSingleCheckbox(question, option) {
              const current = this.formData[question.id] || [];

              const isChecked = current.includes(option.answer_value);
              if (isChecked) {
                this.formData[question.id] = current.filter(v => v !== option.answer_value);
              } else {
                this.formData[question.id] = [...current, option.answer_value];
              }

              this.handleOptionChange(question.id, option, question.question_type);
            },
            async searchGPs() {
                if (this.gpSearchPostcode.length < 3) return;
                try {
                  const response = await fetch(
                    `https://admin.happypharmacy.co.uk/api/v1/data/gp?postcode=${encodeURIComponent(this.gpSearchPostcode)}`
                  );
                  const data = await response.json();
                  this.gpResults = data.results || [];
                } catch (error) {
                  console.error("Failed to fetch GP list:", error);
                  this.gpResults = [];
                }
              },

              selectGP(questionId, gp) {
                this.formData[questionId] = gp;
                this.gpResults = [];
                this.manualGPEntry = false;
              },

              selectManualGP(questionId) {
                const { name, address } = this.manualGP;
                if (!name || !address) {
                  alert("Please enter at least the GP name and address.");
                  return;
                }

                this.formData[questionId] = { ...this.manualGP };
                this.manualGPEntry = false;
              },

            validateFormBeforeSubmit() {
              for (const group of this.consultationGroups) {
                for (const question of group.questions) {
                  if (question.content_type === "rich_content") continue;
                  if (!this.isQuestionVisible(question)) continue;

                  const answer = this.formData[question.id];

                  // Basic required field validation
                  if (["text", "textarea", "dob"].includes(question.question_type)) {
                    if (!answer || answer.trim() === "") return question.id;
                  } else if (question.question_type === "checkbox") {
                    if (!Array.isArray(answer) || answer.length === 0) return question.id;
                  } else if (question.question_type === "file") {
                    // if (!answer) return question.id;
                  } else if (question.question_type === "height") {
                    if (!(answer && (answer.cm || (answer.ft && answer.in)))) return question.id;
                  } else if (question.question_type === "weight") {
                    if (!(answer && (answer.kg || (answer.st && answer.lbs)))) return question.id;
                  } else {
                    if (!answer) return question.id;
                  }

                  // Check selected options for advisory confirmation or extra details
                  if (["radio", "checkbox"].includes(question.question_type)) {
                      const selectedValues = Array.isArray(answer) ? answer : [answer];

                      for (const selectedValue of selectedValues) {
                        const selectedOption = question.options?.find(o => o.answer_value === selectedValue);
                        if (!selectedOption) continue;

                        const selectedOptionId = selectedOption.id;

                        // ❌ Block if a warning is present for this selected option
                        if (
                          this.formWarning[question.id] &&
                          this.formWarning[question.id][selectedOptionId]
                        ) {
                          return question.id; // Will scroll to this question and block submission
                        }

                        // ✅ Advisory confirmation checkbox required
                        if (selectedOption.requires_advisory_confirmation) {
                          if (!this.formConfirmation[question.id]) {
                            return question.id;
                          }
                        }

                        // ✅ Additional free text required
                        if (selectedOption.requires_additional_details) {
                          const extra = this.additionalDetails[question.id];
                          if (!extra || extra.trim() === "") {
                            return question.id;
                          }
                        }
                      }
                    }
                }
              }

            if (this.isBMITooLow) {
                // Find the weight question to highlight it
                const weightQuestion = this.flatQuestions.find(q => q.question_type === 'weight');
                if (weightQuestion) {
                    return weightQuestion.id;
                }
            }

            if(this.isAgeInvalid){
                const dobQuestion = this.flatQuestions.find(q => q.question_type === 'dob');
                if (dobQuestion) {
                    return dobQuestion.id;
                }
            }



                return null; // All valid
            },

            updateProductAccess(productType, productIds) {
                const data = JSON.parse(localStorage.getItem("happyPharmacy") || "{}");
                data.productIdsByType = data.productIdsByType || {};
                data.productIdsByType[productType] = productIds;
                localStorage.setItem("happyPharmacy", JSON.stringify(data));
              },

            resetForm(){
                this.isFormSubmitted = false;
                this.dateSubmitted = null;

                const currentState = JSON.parse(localStorage.getItem(`consultationFormState_${this.consultationType}`) || "{}");
                currentState.isFormSubmitted = this.isFormSubmitted;
                currentState.dateSubmitted = this.dateSubmitted;

                localStorage.setItem(`consultationFormState_${this.consultationType}`, JSON.stringify(currentState));
            },
            submitForm() {

                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: 'consultation_reorder_form_submit',
                    consultation_type: this.consultationType,  // already available as a JS var in the Liquid template
                });

                const firstInvalidId = this.validateFormBeforeSubmit();
                if (firstInvalidId) {
                    this.invalidQuestionId = firstInvalidId;
                    // Optional: Remove highlight after 3 seconds
                    setTimeout(() => {
                      this.invalidQuestionId = null;
                    }, 5000);
                      const el = this.$refs[`question-${firstInvalidId}`]?.[0]; // Vue 3 returns array
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                      return;
                }

                // Example API URL (replace with your actual endpoint):
                const apiUrl = `https://admin.happypharmacy.co.uk/api/v1/consultation/${this.consultationType}`;

                // Prepare the payload
                const payload = {
                    guid: this.formGuid,
                    consultationType: this.consultationType, // Include consultation type
                    answers: this.formData,                 // Form data holding user responses
                    additionalDetails: this.additionalDetails, // Any additional details
                };

                // Send data to the API
                fetch(apiUrl, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json" // Set content type to JSON
                    },
                    body: JSON.stringify(payload)          // Convert payload to JSON string
                })
                    .then((response) => {
                        if (!response.ok) {
                            throw new Error(`API error: ${response.statusText}`);
                        }
                        return response.json();
                    })
                    .then((data) => {
                        console.log("Successfully submitted:", data);
                        this.isFormSubmitted = true;          // Mark the form as submitted
                        this.dateSubmitted = new Date().toISOString();

                        const stateToSave = {
                            formData: this.formData,
                            formAdvisory: this.formAdvisory,
                            formConfirmation: this.formConfirmation,
                            additionalDetails: this.additionalDetails,
                            isFormSubmitted: this.isFormSubmitted, // Mark as submitted
                            dateSubmitted: this.dateSubmitted
                        };
                        localStorage.setItem(`consultationFormState_${this.consultationType}`, JSON.stringify(stateToSave));
                        // Set ProductId's to local storage
                        this.updateProductAccess(this.consultationType, this.productIds);
                    })
                    .catch((error) => {
                        console.error("Error submitting form:", error);
                    });

            },

            loadProducts() {

                let weightLossProducts = [
                    {
                        id: "1",
                        product_id: "7723490082878",
                        variant_id: "41506523283518",
                        title: "Mounjaro Weight Loss Injection",
                        price: "124.99",
                        vendor: "Lilly",
                        link: "/products/mounjaro",
                        image: "/cdn/shop/files/pharmacy-online-mounjaro-mounjaro-1708881903mounjaro-pre-filled-pen.jpg?v=1742744293&width=600",
                    },
                    {
                        id: "2",
                        product_id: "7724985417790",
                        variant_id: "41506498347070",
                        title: "Wegovy Weight Loss Injection",
                        price: "124.99",
                        vendor: "Novo Nordisk",
                        link: "/products/wegovy-weight-loss-injection",
                        image: "/cdn/shop/files/wegovy-01.webp?v=1742744395&width=600",
                    },
                    {
                        id: "3",
                        product_id: "7724986826814",
                        variant_id: "41506523578430",
                        title: "Alli Weight Loss Capsules",
                        price: "39.98",
                        vendor: "Alli",
                        link: "/products/alli-weight-loss-capsules",
                        image: "/cdn/shop/files/Alli.jpg?v=1742847931&width=600",
                    },
                    {
                        id: "4",
                        product_id: "7724986925118",
                        variant_id: "41506524430398",
                        title: "Orlistat Weight Loss Capusles",

                        price: "34.98",
                        vendor: "Activis",
                        link: "/products/orlistat-weight-loss-capusles",
                        image: "/cdn/shop/files/orlistat.jpg?v=1742826715&width=600",
                    },
                    {
                        id: "5",
                        product_id: "8106529488958",
                        variant_id: "42933465972798",
                        title: "Wegovy Weight Loss Pills",
                        price: "99.00",
                        vendor: "Novo Nordisk",
                        link: "/products/wegovy-pills",
                        image: "/cdn/shop/files/1.5mgPack.png?v=1781632344&width=640",
                    },
                    {
                        id: "6",
                        product_id: "8232132444222",
                        variant_id: "43237418074174",
                        title: "Foundayo (Orforglipron) Weight Loss Pills",
                        price: "99.00",
                        vendor: "Lilly",
                        link: "/products/foundayo-orforglipron-weight-loss-pills",
                        image: "/cdn/shop/files/foundaro-bottle.png?v=1787594328&width=640",
                    },
                ];

                let hairLossProducts = [
                    {
                        id: "5",
                        product_id: "7724988104766",
                        variant_id: "41506528198718",
                        title: "Finastride",
                        price: "13.94",
                        vendor: "Activis",
                        link: "/products/finastride",
                        image: "/cdn/shop/files/finasteride.jpg?v=1742826777&width=600",
                    },
                    {
                        id: "6",
                        product_id: "7724988170302",
                        variant_id: "41506528493630",
                        title: "Regaine For Men (Extra Stength Solution)",
                        price: "33.60",
                        vendor: "Regaine",
                        link: "/products/regaine-for-men-extra-stength-solution",
                        image: "/cdn/shop/files/Regaine_For_Men.jpg?v=1742819731&width=600",
                    }
                ];

                let medicationProducts = [
                    {
                        id: "7",
                        product_id: "7724988235838",
                        variant_id: "41506529280062",
                        title: "Regaine For Women Extra Stength Solution",
                        price: "27.77",
                        vendor: "Regaine",
                        link: "/products/regaine-for-women-extra-stength-solution",
                        image: "/cdn/shop/files/regaine-for-women.jpg?v=1742848007&width=600",
                    }
                ];

                let erectileDysfunctionProducts = [
                    {
                        id: "8",
                        product_id: "7724987973694",
                        variant_id: "41506527150142",
                        title: "Viagra",
                        price: "26.98",
                        vendor: "Pfizer",
                        link: "/products/viagra",
                        image: "/cdn/shop/files/viagra.jpg?v=1742827148&width=600",
                    },
                    {
                        id: "9",
                        product_id: "7724988006462",
                        variant_id: "41506527543358",
                        title: "Viagra Connect",
                        price: "23.99",
                        vendor: "Pfizer",
                        link: "/products/viagra-connect",
                        image: "/cdn/shop/files/viagra-connect.jpg?v=1742827048&width=600",
                    },
                    {
                        id: "10",
                        product_id: "7724988039230",
                        variant_id: "41506527641662",
                        title: "Cialis Together",
                        price: "18.33",
                        vendor: "Cialis",
                        link: "/products/cialis-together",
                        image: "/cdn/shop/files/cialis.jpg?v=1742826810&width=600",
                    },
                    {
                        id: "11",
                        product_id: "7724986990654",
                        variant_id: "41506524790846",
                        title: "Sildenafil",
                        price: "5.98",
                        vendor: "Teva",
                        link: "/products/sildenafil",
                        image: "/cdn/shop/files/sildenafil.jpg?v=1742826650&width=600",
                    },
                    {
                        id: "12",
                        product_id: "7774371840062",
                        variant_id: "41676027527230",
                        title: "Tadalafil",
                        price: "24.99",
                        vendor: "Teva",
                        link: "/products/tadalafil-one-a-day-tablets",
                        image: "/cdn/shop/files/tadalafil-v3-front.jpg?v=1748955709&width=600",
                    },
                    {
                        id: "13",
                        product_id: "7790407254078",
                        variant_id: "41736379170878",
                        title: "Tadalafil",
                        price: "8.95",
                        vendor: "Teva",
                        link: "/products/tadalafil-teva",
                        image: "/cdn/shop/files/tadalafil-v2-front_66291e16-4bb7-4280-9b57-a48ac9a4d834.jpg?v=1748955867&width=600",
                    },
                    {
                        id: "14",
                        product_id: "7790407745598",
                        variant_id: "41736365113406",
                        title: "Tadalafil",
                        price: "9.49",
                        vendor: "Generic",
                        link: "/products/tadalafil-teva-20mg",
                        image: "/cdn/shop/files/tadalafil-v5-front.jpg?v=1748956063&width=600",
                    }
                ];

                let productData = [];
                if (this.consultationType === "erectile-dysfunction") {
                    productData = erectileDysfunctionProducts;
                } else if (this.consultationType === "medication") {
                    productData = medicationProducts;
                } else if (this.consultationType === "hair-loss") {
                    productData = hairLossProducts;
                } else {
                    productData = weightLossProducts;
                }
                this.products = this.productData;

                // Extract product IDs from the product data
                this.productIds = productData.map(p => p.product_id);
            },

        },
        mounted() {

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'consultation_reorder_form_view',
                consultation_type: this.consultationType,
            });

            const existingGuid = localStorage.getItem("hp_form_guid");
            if (existingGuid) {
                this.formGuid = existingGuid;
            } else {
                this.formGuid = crypto.randomUUID();
                localStorage.setItem("hp_form_guid", this.formGuid);
            }

            fetch('/cart/update.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    attributes: {
                        form_submission_uuid: this.formGuid
                    }
                })
            });


            const savedState = localStorage.getItem(`consultationFormState_${this.consultationType}`);
            if (savedState) {
                console.log("Restoring form state:", savedState);
                const parsedState = JSON.parse(savedState);
                this.previousFormData = parsedState.formData || {};
                this.formAdvisory = parsedState.formAdvisory || {};
                this.formConfirmation = parsedState.formConfirmation || {};
                this.isFormSubmitted = parsedState.isFormSubmitted || false;
                this.dateSubmitted = parsedState.dateSubmitted || null;
                this.additionalDetails = parsedState.additionalDetails || {};

                this.checkFormSubmissionAge();

            }

            this.fetchConsultationData();

        },
        watch: {
            isFormSubmitted(newVal) {
            if (newVal) {
              this.$nextTick(() => {
                const section = this.$refs.thankYouSection;
                console.log("Scrolling to thank-you section:", section);
                if (section) {
                  section.scrollIntoView({ behavior: 'smooth' });
                }
              });
            }
          }

        },
    }).mount(root);
});
